import React, { ChangeEvent, useEffect, useState } from 'react';
import isEmail from 'validator/lib/isEmail';

import { AttentionMessage, InfoMessage } from '@eika/attention-message';
import { SmallButton } from '@eika/button';
import { ButtonWithSpinner } from '@eika/button-with-spinner';
import { IconTypes } from '@eika/icon';
import { Input } from '@eika/input';
import { ErrorLabel } from '@eika/label';
import { Link } from '@eika/link';
import { Loader } from '@eika/loader';
import { ToggleSwitch } from '@eika/toggle-switch';

import { Content } from '../../components/shared/Content';
import { useConfig } from '../../defaultConfig';
import { useContactInformation } from '../../fetchHooks/useContact';
import { useEfakturaAgreement, useEfakturaNotification } from '../../fetchHooks/useEfaktura';
import useTranslation from '../../hooks/useTranslation';

import './EFakturaReservations.scss';

const EFakturaNotifications: React.FC = () => {
    const translated = useTranslation();
    const [checked, setChecked] = useState(false);
    const [email, setEmail] = useState('');
    const [validationMessage, setValidationMessage] = useState<string>();
    const { data, isLoading, error } = useEfakturaAgreement();
    const contactInformation = useContactInformation();
    const showWarning = checked && email !== contactInformation.data?.email?.value;
    const { contactInfo: contactInfoLink } = useConfig();

    const {
        disableNotification,
        enableNotification,
        isSaving,
        error: updateError,
        isSuccess,
    } = useEfakturaNotification();

    const handleEmailChange = (event: ChangeEvent<HTMLInputElement>) => setEmail(event.target.value);

    useEffect(() => {
        setEmail(data?.email?.value || contactInformation.data?.email?.value || '');
        setChecked(data?.enableNotification ?? false);
    }, [contactInformation.data?.email?.value, data?.email, data?.enableNotification]);

    const getValidations = (newValue: boolean) => {
        if (newValue) {
            if (!email) {
                return translated.eFakturaAgreement.emailRequired;
            }
            if (!isEmail(email)) {
                return translated.eFakturaAgreement.emailValidation;
            }
        }

        return undefined;
    };

    const validate = (newValue?: boolean) => {
        const errors = getValidations(newValue ?? checked);
        setValidationMessage(errors);
        return !errors;
    };

    const submit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!validate()) return;
        if (checked) {
            enableNotification.mutate({ value: email });
        }
    };

    const onChange = (value: boolean) => {
        setChecked(value);
        if (value) {
            validate(value);
        } else {
            disableNotification.mutate();
        }
    };

    useEffect(() => {
        if (!isSuccess) return undefined;
        const timeout = setTimeout(() => {
            disableNotification.reset();
            enableNotification.reset();
        }, 2000);
        return () => timeout && clearTimeout(timeout);
    }, [disableNotification, enableNotification, isSuccess]);

    const hasChange =
        checked && (data?.enableNotification === false || email !== (data?.email?.value ?? '') || isSuccess);

    return (
        <Content>
            <div className="efaktura-notification__email">
                <form onSubmit={submit}>
                    <h3>{translated.eFakturaNotifications.toggleEmailLabel}</h3>
                    {isLoading ? (
                        <div className="efaktura-notification__toggle-spinner">
                            <Loader mini />
                        </div>
                    ) : (
                        <div className="efaktura-notification__email_form">
                            <div>
                                {data?.hasAgreement && data?.enabled ? (
                                    <ToggleSwitch
                                        checked={checked}
                                        onChange={onChange}
                                        isLoading={!checked && isSaving}
                                    />
                                ) : (
                                    <div className="efaktura-notification__toggle-off">
                                        {translated.eFakturaNotifications.off}
                                    </div>
                                )}
                            </div>
                            {checked && (
                                <div>
                                    <Input
                                        label={translated.eFakturaAgreement.emailField.label}
                                        placeholder={translated.eFakturaAgreement.emailField.placeholder}
                                        type="email"
                                        inputMode="email"
                                        value={email}
                                        onChange={handleEmailChange}
                                        error={validationMessage}
                                        className="efaktura-notification__email-input"
                                        id="email-field-input"
                                    />
                                </div>
                            )}
                            {showWarning && (
                                <AttentionMessage
                                    type="info"
                                    header={translated.eFakturaNotifications.differentEmailWarning.header}
                                    text={translated.eFakturaNotifications.differentEmailWarning.text}
                                    icon={IconTypes.UTROPSTEGN_24}
                                    clickableItem={
                                        <Link linkType="button-negative" href={contactInfoLink?.url}>
                                            {translated.eFakturaNotifications.differentEmailWarning.linkText}
                                        </Link>
                                    }
                                />
                            )}
                            {hasChange && (
                                <div>
                                    <ButtonWithSpinner
                                        buttonType="positive"
                                        type="submit"
                                        showSpinner={isSaving}
                                        showSuccess={isSuccess}
                                    >
                                        {translated.save}
                                    </ButtonWithSpinner>
                                </div>
                            )}
                        </div>
                    )}
                </form>
                <div role="alert">
                    {(!!updateError || !!error) && (
                        <ErrorLabel
                            className="efaktura-notification__email-error"
                            icon={IconTypes.UTROPSTEGN_24}
                            text={translated.eFakturaAgreement.errorLabel}
                        />
                    )}
                </div>
            </div>
            {(!data?.hasAgreement || !data.enabled) && !isLoading && (
                <InfoMessage
                    text={translated.eFakturaNotifications.noAgreementInfo}
                    clickableItem={
                        <SmallButton buttonType="negative" onClick={() => window.history.back()}>
                            {translated.eFakturaNotifications.goBack}
                        </SmallButton>
                    }
                />
            )}
        </Content>
    );
};

export default EFakturaNotifications;
